import { ReplayControls } from '@bn-games/ui-shell';
import type { GameStateData } from '../game/GameState.js';
import {
  seatsFor,
  type DominoPlayer,
} from '../game/types.js';
import { t } from '../i18n/I18n.js';
import { computeZones } from '../layout/LayoutManager.js';
import {
  computeChainLayout,
  drawChain,
  drawChainRegionRecess,
  drawTable,
} from '../render/BoardRenderer.js';
import {
  drawHandZoneDividers,
  drawHandZoneFrame,
  drawLocalRack,
  drawPlayerInfo,
  layoutLocalRack,
  type HandZone,
  type PlayerInfo,
} from '../render/HandRenderer.js';
import { drawTurnBanner } from '../render/OverlayRenderer.js';
import type { RenderPalette } from '../render/palette.js';
import { scoreLabelKey, seatDisplayScore } from '../ui/scoreDisplay.js';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '../utils/constants.js';
import { ReplayController } from './ReplayController.js';
import type { GameRecord } from './types.js';

export type ReplayReturnTarget = 'history' | 'menu';

interface ReplaySceneDeps {
  readonly requestRender: () => void;
  readonly onExit: (target: ReplayReturnTarget) => void;
}

const AUTO_PLAY_DELAY_MS = 600;

/**
 * Owns the replay UI lifecycle: opens a `ReplayController` for a `GameRecord`,
 * mounts the shared `ReplayControls`, and renders the recomputed game state
 * into the host canvas. Full-info replay — every seat's hand is drawn face-up
 * (the engine state is the server-authoritative full view).
 */
export class ReplayScene {
  private readonly deps: ReplaySceneDeps;
  private controller: ReplayController | null = null;
  private controls: ReplayControls | null = null;
  private returnTo: ReplayReturnTarget = 'menu';
  private isPlaying = false;
  private speed = 1;
  private playTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(deps: ReplaySceneDeps) {
    this.deps = deps;
  }

  get isOpen(): boolean {
    return this.controller !== null;
  }

  open(record: GameRecord, returnTo: ReplayReturnTarget): void {
    if (this.controller) this.close();

    this.returnTo = returnTo;
    this.speed = 1;
    this.isPlaying = false;

    const controller = new ReplayController(record);
    controller.setListener(() => {
      this.updateControlsState();
      this.deps.requestRender();
    });
    this.controller = controller;

    this.controls = new ReplayControls(
      {
        onFirst: () => this.seek(0),
        onPrev: () => this.seek(controller.getStepIndex() - 1),
        onPlayPause: () => this.togglePlay(),
        onNext: () => this.manualNext(),
        onLast: () => this.seek(controller.getTotalSteps()),
        onSpeed: (s) => this.setSpeed(s),
        onBack: () => this.exit(),
      },
      this.controlsState(),
    );
    this.controls.mount();
  }

  close(): void {
    this.cancelAutoPlay();
    this.controls?.unmount();
    this.controls = null;
    this.controller = null;
    this.isPlaying = false;
  }

  exit(): void {
    const target = this.returnTo;
    this.close();
    this.deps.onExit(target);
  }

  /**
   * Draw the scene onto the host canvas. Called each frame from main.ts'
   * render loop while replay is the active scene.
   */
  render(ctx: CanvasRenderingContext2D, palette: RenderPalette): void {
    const controller = this.controller;
    if (!controller) return;
    const record = controller.getRecord();
    const state = controller.getState();

    drawTable(ctx, CANVAS_WIDTH, CANVAS_HEIGHT, palette);

    const { chainRegion, seatZones } = computeZones(record.playerCount);
    drawChainRegionRecess(ctx, chainRegion, palette);
    const chainLayout = computeChainLayout(state.chain, chainRegion, controller.getTurns());
    drawChain(ctx, chainLayout, palette);

    this.drawAllHands(ctx, state, record, seatZones, palette);

    drawTurnBanner(ctx, { text: this.bannerLabel(state, record) }, palette);
  }

  private drawAllHands(
    ctx: CanvasRenderingContext2D,
    state: GameStateData,
    record: GameRecord,
    seatZones: Readonly<Record<DominoPlayer, HandZone | null>>,
    palette: RenderPalette,
  ): void {
    // Map physical positions (south/west/north/east) to engine seat indices
    // from the recorded local viewer's perspective. Seat 0 sits south when no
    // local seat was recorded (hot-seat).
    const localSeat = Math.min(
      Math.max(0, record.seed.localSeat ?? 0),
      record.playerCount - 1,
    );
    const positions: readonly DominoPlayer[] =
      record.playerCount === 2
        ? ['south', 'north']
        : record.playerCount === 3
          ? ['south', 'west', 'north']
          : ['south', 'west', 'north', 'east'];

    const activeSeat = state.pendingMuggins
      ? state.pendingMuggins.scorerSeat
      : state.currentSeat;
    for (let i = 0; i < positions.length; i += 1) {
      const position = positions[i]!;
      const zone = seatZones[position];
      if (!zone) continue;
      const seatIndex = (localSeat + i) % record.playerCount;
      const hand = state.hands[seatIndex] ?? [];
      drawHandZoneFrame(ctx, zone, palette);
      const placements = layoutLocalRack(zone, hand);
      drawLocalRack(ctx, placements, palette);
    }
    drawHandZoneDividers(ctx, seatZones);

    for (let i = 0; i < positions.length; i += 1) {
      const position = positions[i]!;
      const zone = seatZones[position];
      if (!zone) continue;
      const seatIndex = (localSeat + i) % record.playerCount;
      const hand = state.hands[seatIndex] ?? [];
      drawPlayerInfo(ctx, position, this.playerInfo(seatIndex, hand.length, state, activeSeat, record, localSeat), palette);
    }
  }

  private playerInfo(
    seatIndex: number,
    tileCount: number,
    state: GameStateData,
    activeSeat: number,
    record: GameRecord,
    localSeat: number,
  ): PlayerInfo {
    const recordedName = record.seed.playerName?.trim();
    const name =
      seatIndex === localSeat && recordedName
        ? recordedName
        : t('result.seatNumber', { number: seatIndex + 1 });
    return {
      name,
      score: seatDisplayScore(state, seatIndex),
      tileCount,
      isActive: seatIndex === activeSeat && !state.handOver,
      scoreLabel: t(scoreLabelKey(state)),
      tilesLabel: t('playerInfo.tiles'),
    };
  }

  private actorTag(seatIndex: number, record: GameRecord): string {
    const seatName = seatsFor(record.playerCount)[seatIndex] ?? 'south';
    return `${t('result.seatNumber', { number: seatIndex + 1 })} (${t(`seats.${seatName}`)})`;
  }

  private lastActionPrefix(state: GameStateData, record: GameRecord): string {
    const last = state.lastAction;
    if (last === null) return '';
    const actor = this.actorTag(last.seat, record);
    switch (last.kind) {
      case 'play': {
        const a = last.tile[0];
        const b = last.tile[1];
        if (last.wentOut && last.points > 0) {
          return t('banner.lastPlayScoredWentOut', { actor, a, b, points: last.points });
        }
        if (last.wentOut) {
          return t('banner.lastPlayWentOut', { actor, a, b });
        }
        if (last.points > 0) {
          return t('banner.lastPlayScored', { actor, a, b, points: last.points });
        }
        return t('banner.lastPlay', { actor, a, b });
      }
      case 'draw':
        return t('banner.lastDraw', { actor });
      case 'pass':
        return t('banner.lastPass', { actor });
      case 'claim-muggins': {
        if (last.tile !== undefined) {
          const a = last.tile[0];
          const b = last.tile[1];
          return t('banner.lastClaimWithTile', { actor, points: last.points, a, b });
        }
        return t('banner.lastClaim', { actor, points: last.points });
      }
    }
  }

  private bannerLabel(state: GameStateData, record: GameRecord): string {
    if (state.matchOver) return t('banner.matchOver');
    if (state.handOver) return t('banner.handOver');
    const seat = state.pendingMuggins ? state.pendingMuggins.scorerSeat : state.currentSeat;
    const seatTag = this.actorTag(seat, record);
    const turnText = state.pendingMuggins
      ? t('banner.claimMuggins', { seat: seatTag })
      : t('banner.seatTurn', { seat: seatTag });
    const prefix = this.lastActionPrefix(state, record);
    return prefix.length > 0 ? `${prefix} ${turnText}` : turnText;
  }

  private controlsState() {
    const c = this.controller;
    return {
      stepIndex: c ? c.getStepIndex() : 0,
      totalSteps: c ? c.getTotalSteps() : 0,
      speed: this.speed,
      isPlaying: this.isPlaying,
      backLabel:
        this.returnTo === 'history'
          ? t('replay.controls.backToHistory')
          : t('replay.controls.backToMenu'),
    };
  }

  private updateControlsState(): void {
    this.controls?.update(this.controlsState());
  }

  private setSpeed(next: number): void {
    if (next === this.speed) return;
    this.speed = next;
    this.updateControlsState();
    if (this.isPlaying) this.scheduleAutoNext();
  }

  private seek(target: number): void {
    if (!this.controller) return;
    this.cancelAutoPlay();
    this.controller.seek(target);
    this.updateControlsState();
    this.deps.requestRender();
  }

  private manualNext(): void {
    if (!this.controller) return;
    this.cancelAutoPlay();
    this.controller.stepForward();
    this.deps.requestRender();
  }

  private togglePlay(): void {
    if (!this.controller) return;
    if (this.isPlaying) {
      this.isPlaying = false;
      this.cancelAutoPlay();
      this.updateControlsState();
      return;
    }
    if (this.controller.isAtEnd()) return;
    this.isPlaying = true;
    this.updateControlsState();
    // Step immediately on Play; subsequent steps are timer-paced.
    this.controller.stepForward();
    this.scheduleAutoNext();
  }

  private scheduleAutoNext(): void {
    this.cancelAutoPlay();
    if (!this.controller || this.controller.isAtEnd()) {
      this.isPlaying = false;
      this.updateControlsState();
      return;
    }
    this.playTimer = setTimeout(() => {
      this.playTimer = null;
      if (!this.isPlaying || !this.controller) return;
      if (this.controller.isAtEnd()) {
        this.isPlaying = false;
        this.updateControlsState();
        return;
      }
      this.controller.stepForward();
      this.scheduleAutoNext();
    }, Math.max(80, AUTO_PLAY_DELAY_MS / this.speed));
  }

  private cancelAutoPlay(): void {
    if (this.playTimer === null) return;
    clearTimeout(this.playTimer);
    this.playTimer = null;
  }
}
